import { useCallback } from 'react';
import {
  OfficeExclusiveType,
  useMediaStore,
} from '@brightmls/nextgen.lae.mediacenter';
import {
  useFormStoreEvents,
  useMediaResource,
} from '@brightmls/nextgen.lae.flc';
import { MediaCategory } from '../../../../system/media';
import { useMediaLogger } from './useMediaLogger';
import type { MediaJob } from '../../../../hooks/useMediaSubscription';

/**
 * Hook that provides handlers for media job updates from WebSocket
 * Extracted from MediaCenterDataLoader to reduce complexity
 */
export function useMediaJobHandlers() {
  const { media: flcMedia, updateMediaByCategory } = useMediaResource();
  const { updateFromListingStateAfter } = useFormStoreEvents();
  const updateProcessingItem = useMediaStore(
    (state) => state.updateProcessingItem,
  );
  const updateItemKey = useMediaStore((state) => state.updateItemKey);
  const logger = useMediaLogger('useMediaJobHandlers');

  /**
   * Handle job completion - update item key and sync to FLC
   * Called when MEDIA_JOB_COMPLETE is received
   */
  const handleJobComplete = useCallback(
    (job: MediaJob) => {
      logger.log('Job complete:', {
        jobId: job.jobId,
        mediaKey: job.mediaKey,
        resultUrl: job.resultUrl,
        hasFinalMediaRecord: !!job.finalMediaRecord,
        aiStatus: job.aiStatus,
      });

      // Get AI description from the completed item in store (set during MEDIA_AI_UPDATE)
      const aiDescription =
        job.generateDescriptions &&
        job.aiResults &&
        job.aiResults.image_description?.description
          ? job.aiResults.image_description?.description
          : undefined;

      // Check if AI is still processing (scanning, pending, etc.)
      const aiStillProcessing =
        job.aiStatus &&
        !['completed', 'failed', 'skipped', 'scanned', 'applied'].includes(
          job.aiStatus,
        );

      // First, update the processing item with completion status
      // Keep _isProcessing true if AI is still scanning
      // Spread finalMediaRecord so all URL variants (MEDIA_URL_MEDIUM, etc.) get updated
      // in the store — usePrimaryPhoto prefers MEDIA_URL_MEDIUM for header display
      // Merge AI description AFTER the spread so a null from a stale finalMediaRecord
      // doesn't overwrite the description that was already set via MEDIA_AI_UPDATE
      // Clear _localPreviewUrl so the embed uses final MLS URLs instead of stale blob URL
      updateProcessingItem(job.jobId, {
        ...(job.finalMediaRecord || {}),
        _status: 'completed',
        _aiStatus: job.aiStatus,
        _isProcessing: aiStillProcessing,
        _resultUrl: job.resultUrl,
        _localPreviewUrl: undefined,
        MEDIA_URL: job.resultUrl,
        ...(aiDescription && { MEDIA_SHORT_DESCRIPTION: aiDescription }),
      } as any);

      // Update listing state for OfficeExclusive validations
      if (job.docType === OfficeExclusiveType.OFFICE_EXCLUSIVE) {
        updateFromListingStateAfter(100);
      }

      // If we have a new mediaKey from the server, update the item key
      const newKey = job.finalMediaRecord?.MED_VW_KEY || job.mediaKey;
      const newMediaKey = job.finalMediaRecord?.MEDIA_KEY || newKey;
      if (newKey && newKey !== job.jobId) {
        logger.log('Updating item key:', {
          oldKey: job.jobId,
          newKey: newKey,
        });
        updateItemKey(job.jobId, newKey, newMediaKey);
      }

      // Sync to FLC using finalMediaRecord if available, otherwise build from job data
      let flcResource: any;

      if (job.finalMediaRecord && job.finalMediaRecord.MED_VW_KEY) {
        // Use finalMediaRecord directly (NGW format), merge in AI description
        flcResource = {
          ...job.finalMediaRecord,
          // Merge AI description if AI descriptions was enabled and we have one
          ...(aiDescription && { MEDIA_SHORT_DESCRIPTION: aiDescription }),
        };

        // Override display order from store — the store is the source of truth
        // for ordering (user may have reordered while the job was processing)
        const storeItem = useMediaStore
          .getState()
          .items.find(
            (item) =>
              item.MED_VW_KEY === flcResource.MED_VW_KEY ||
              item._jobId === job.jobId,
          );
        if (storeItem) {
          flcResource.MEDIA_DISPLAY_ORDER = storeItem.MEDIA_DISPLAY_ORDER;
          flcResource.MEDIA_ITEM_NUMBER = storeItem.MEDIA_ITEM_NUMBER;
        }

        logger.log('Using finalMediaRecord for FLC sync:', {
          key: flcResource.MED_VW_KEY,
          hasAiDescription: !!aiDescription,
        });
      } else {
        // Fallback: build from job data (for backwards compatibility)
        const completedItem = useMediaStore
          .getState()
          .items.find((item) => item._jobId === job.jobId);
        if (!completedItem) {
          logger.warn('Could not find completed item for FLC sync');
          return;
        }

        flcResource = {
          MED_VW_KEY: job.mediaKey || job.jobId,
          MEDIA_KEY: job.mediaKey || job.jobId,
          MEDIA_CATEGORY: job.category,
          MEDIA_DISPLAY_ORDER:
            job.mediaDisplayOrder ?? completedItem.MEDIA_DISPLAY_ORDER,
          MEDIA_ITEM_NUMBER:
            job.mediaItemNumber ?? completedItem.MEDIA_ITEM_NUMBER,
          // Use AI description if available, otherwise use the item's description
          MEDIA_SHORT_DESCRIPTION:
            aiDescription ?? completedItem.MEDIA_SHORT_DESCRIPTION,
          MEDIA_LONG_DESCRIPTION: completedItem.MEDIA_LONG_DESCRIPTION,
          MEDIA_URL: job.resultUrl,
          MEDIA_VISIBILITY: job.visibility ?? completedItem.MEDIA_VISIBILITY,
          MEDIA_IMAGE_WIDTH:
            job.imageMetadata?.width ?? completedItem.MEDIA_IMAGE_WIDTH,
          MEDIA_IMAGE_HEIGHT:
            job.imageMetadata?.height ?? completedItem.MEDIA_IMAGE_HEIGHT,
          MEDIA_MODIFICATION_TIMESTAMP:
            job.updatedAt || new Date().toISOString(),
        };
      }

      // Determine the category for FLC update
      const category = job.finalMediaRecord?.MEDIA_CATEGORY || job.category;
      let mediaCategory: MediaCategory | undefined;
      if (category === 'Photo') {
        mediaCategory = MediaCategory.Photo;
      } else if (category === 'Document') {
        mediaCategory = MediaCategory.Document;
      } else if (category === 'Floor Plan') {
        mediaCategory = MediaCategory.FloorPlan;
      }

      if (mediaCategory && flcResource) {
        // Get current FLC media for this category
        const currentFlcMedia = (flcMedia as any[]).filter(
          (m) => m.MEDIA_CATEGORY === category,
        );

        // Add the new resource (or update if key already exists)
        const existingIndex = currentFlcMedia.findIndex(
          (m) => m.MED_VW_KEY === flcResource.MED_VW_KEY,
        );

        let updatedMedia;
        if (existingIndex >= 0) {
          updatedMedia = [...currentFlcMedia];
          updatedMedia[existingIndex] = flcResource;
        } else {
          updatedMedia = [...currentFlcMedia, flcResource];
        }

        logger.log('Syncing completed item to FLC:', {
          category: mediaCategory,
          itemCount: updatedMedia.length,
          key: flcResource.MED_VW_KEY,
        });

        updateMediaByCategory({
          category: mediaCategory,
          resources: updatedMedia,
        });
      }
    },
    [
      updateProcessingItem,
      updateItemKey,
      flcMedia,
      updateMediaByCategory,
      logger,
    ],
  );

  /**
   * Handle WebSocket job updates
   * Maps MediaJob fields to MediaItem fields for store update
   */
  const handleJobUpdate = useCallback(
    (job: MediaJob) => {
      logger.log('Job update received:', {
        jobId: job.jobId,
        status: job.status,
        aiStatus: job.aiStatus,
        failedStep: job.failedStep,
        mediaKey: job.mediaKey,
        isAIError: job.isAIError,
      });

      // Read fresh from store — the closure `items` may be stale if
      // handleJobComplete already ran in the same tick before React re-rendered.
      const currentItem = useMediaStore
        .getState()
        .items.find(
          (item) => item.MED_VW_KEY === job.jobId || item._jobId === job.jobId,
        );
      if (currentItem?._isFailed && job.status !== 'failed') {
        logger.log('Ignoring update for already failed item:', {
          jobId: job.jobId,
          currentStatus: 'failed',
          incomingStatus: job.status,
        });
        return;
      }

      // Don't let late MEDIA_UPDATEs overwrite a completed item
      if (currentItem?._status === 'completed') {
        logger.log('Ignoring update for already completed item:', {
          jobId: job.jobId,
          currentStatus: 'completed',
          incomingStatus: job.status,
        });
        return;
      }

      // Extract AI description if available and AI descriptions were requested
      let aiDescription: string | undefined;
      if (
        job.generateDescriptions &&
        job.aiStatus === 'scanned' &&
        job.aiResults?.image_description?.description
      ) {
        aiDescription = job.aiResults.image_description.description;
        logger.log('AI description received:', aiDescription);
      }

      // Determine if still processing:
      // - Job is not in terminal state (completed/failed)
      // - OR AI is still scanning (even if job is submitted)
      const jobStillProcessing = !['completed', 'failed', 'submitted'].includes(
        job.status,
      );
      const aiStillProcessing =
        job.aiStatus &&
        !['completed', 'failed', 'skipped', 'scanned', 'applied'].includes(
          job.aiStatus,
        );
      const isProcessing = jobStillProcessing || aiStillProcessing;

      updateProcessingItem(job.jobId, {
        MEDIA_CATEGORY: job.category,
        MEDIA_SOURCE_FILE_NAME: job.fileName,
        MEDIA_DISPLAY_ORDER: job.mediaDisplayOrder,
        MEDIA_ITEM_NUMBER: job.mediaItemNumber,
        MEDIA_VISIBILITY: job.visibility,
        // Update SHORT_DESCRIPTION from AI if available
        ...(aiDescription && { MEDIA_SHORT_DESCRIPTION: aiDescription }),
        // Job status fields
        _status: job.status,
        _aiStatus: job.aiStatus,
        _isProcessing: isProcessing,
        _isFailed: job.status === 'failed',
        _failedStep: job.failedStep,
        _progress: job.progress,
        _previewUrl: job.previewUrl,
        _processedUrl: job.processedUrl,
        _resultUrl: job.resultUrl,
        _errorMessage: job.error,
        _canRetry: job.canRetry,
        _retryCount: job.retryCount,
        _aiRetryCount: job.aiRetryCount,
        // AI and metadata
        _aiResults: job.aiResults,
        _imageMetadata: job.imageMetadata,
        _pdfMetadata: job.pdfMetadata,
        MEDIA_MODIFICATION_TIMESTAMP: job.updatedAt || job.createdAt,
      } as any);

      // If the job carries a real MLS key (e.g. "submitted" status),
      // update the item key so delete and other operations work
      // even if handleJobComplete's key update is lost to re-subscription churn.
      const newKey = job.finalMediaRecord?.MED_VW_KEY || job.mediaKey;
      if (newKey && newKey !== job.jobId) {
        const newMediaKey = job.finalMediaRecord?.MEDIA_KEY || newKey;
        logger.log('Updating item key from job update:', {
          oldKey: job.jobId,
          newKey,
        });
        updateItemKey(job.jobId, newKey, newMediaKey);
      }
    },
    [updateProcessingItem, updateItemKey, logger],
  );

  /**
   * Route job updates to appropriate handlers
   */
  const handleJobMessage = useCallback(
    (job: MediaJob) => {
      // Route to completion handler for completed jobs
      if (job.status === 'completed') {
        handleJobComplete(job);
      } else {
        // All other updates go to the regular handler
        handleJobUpdate(job);
      }
    },
    [handleJobComplete, handleJobUpdate],
  );

  return {
    handleJobMessage,
    handleJobComplete,
    handleJobUpdate,
  };
}
